import {
  BugAntIcon,
  ChartPieIcon,
  CreditCardIcon,
  CursorArrowRaysIcon,
  DocumentTextIcon,
  FingerPrintIcon,
  KeyIcon,
  LightBulbIcon,
  PencilIcon,
  SquaresPlusIcon,
  QuestionMarkCircleIcon,
  BanknotesIcon,
  UserPlusIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { DestructiveTrashIcon } from "@/components/ui/DestructiveTrashIcon";
import type { Language } from "@/i18n/translations";
import { ESTATE_LIMITS } from "@/utils/membershipLimits";

const pick = <TCs, TEn>(
  language: Language,
  values: { cs: TCs; en: TEn },
): TCs | TEn => values[language];

export const HERO_BADGES = [
  "14 dní zdarma",
  "Bez závazků",
  "Okamžité notifikace",
  "Levnější než jedna káva",
] as const;

export const TRUST_STATS = [
  { value: "24 hodin", label: "monitoring každý den" },
  { value: "všechny", label: "důležité realitní weby" },
  { value: "max 1 min", label: "zpoždění notifikace" },
  { value: "zdarma", label: "14 dní bez závazků" },
] as const;

export const FOOTER_NAV_LINKS = [
  { label: "Co to je?", href: "/#produkty" },
  { label: "Ceník", href: "/#cenik" },
  { label: "Ukázka", href: "/#ukazka" },
  { label: "Blog", href: "/blog" },
] as const;

export const LEGAL_LINKS = [
  { title: "Obchodní podmínky", href: "/obchodni-podminky" },
  { title: "Podmínky předplatného", href: "/podminky-predplatneho" },
  { title: "Zpracování osobních údajů", href: "/zpracovani-osobnich-udaju" },
] as const;

export const INFO_ROWS = [
  { label: "Email", value: "info@softiq.cz" },
  { label: "Podnikající subjekt", value: "Ing. Martin Švejda" },
  { label: "IČ", value: "21086231" },
  { label: "Sídlo", value: "Janského 559/13, 779 00, Olomouc – Povel" },
] as const;

export const PRODUCT_MENU_ITEMS = [
  {
    name: "Základní",
    description:
      "1 nemovitost v portfoliu. 1 hlídací pes na nové nabídky na trhu.",
    href: "/#cenik",
    icon: ChartPieIcon,
  },
  {
    name: "Standardní",
    description:
      "Až 2 nemovitosti v portfoliu. Až 2 hlídací psi na nové nabídky na trhu.",
    href: "/#cenik",
    icon: CursorArrowRaysIcon,
  },
  {
    name: "Premiové",
    description:
      "Až 5 nemovitostí v portfoliu. Až 3 hlídací psi na nové nabídky na trhu.",
    href: "/#cenik",
    icon: FingerPrintIcon,
  },
  {
    name: "Profesionální",
    description:
      "Až 15 nemovitostí v portfoliu. Až 5 hlídacích psů na nové nabídky na trhu.",
    href: "/#cenik",
    icon: SquaresPlusIcon,
  },
] as const;

export const PROFILE_ITEMS = [
  { name: "Změnit členství", href: "", icon: PencilIcon },
  { name: "Změnit platební údaje", href: "", icon: CreditCardIcon },
  { name: "Změnit fakturační údaje", href: "", icon: DocumentTextIcon },
  { name: "Změnit heslo", href: "", icon: KeyIcon },
  { name: "Smazat účet", href: "", icon: DestructiveTrashIcon },
] as const;

export const SETTINGS_ITEMS = [
  { name: "Nahlásit chybu", href: "", icon: BugAntIcon },
  { name: "Požádat o novou funkci", href: "", icon: LightBulbIcon },
] as const;

export const NAVBAR_ITEMS = [
  { name: "Co to je?", href: "/#produkty", icon: QuestionMarkCircleIcon },
  { name: "Portfolio nemovitostí", href: "/portfolio", icon: ChartPieIcon },
  { name: "Hlídací pes", href: "/hlidaci-pes", icon: CursorArrowRaysIcon },
  { name: "Přihlášení", href: "/#prihlaseni", icon: UserIcon },
  { name: "Registrace", href: "/#prihlaseni", icon: UserPlusIcon },
  { name: "Ceník", href: "/#cenik", icon: BanknotesIcon },
  { name: "Ukázka", href: "/#ukazka", icon: CursorArrowRaysIcon },
  { name: "Časté otázky", href: "/#faq", icon: QuestionMarkCircleIcon },
  { name: "Blog", href: "/blog", icon: DocumentTextIcon },
] as const;

const getPortfolioPricingFeatures = (estateLimit: number) =>
  [
    `${estateLimit === 1 ? "1 nemovitost" : `Až ${estateLimit} ${estateLimit < 5 ? "nemovitosti" : "nemovitostí"}`} v portfoliu`,
    "Přehled provozních výdajů",
    "Daňový přehled",
    "Odhad tržní ceny",
    "Hlídání ceny nájmu",
  ] as const;

const getPortfolioPricingFeaturesEn = (estateLimit: number) =>
  [
    `${estateLimit === 1 ? "1 property" : `Up to ${estateLimit} properties`} in your portfolio`,
    "Operating expense overview",
    "Tax overview",
    "Market price estimate",
    "Rent price monitoring",
  ] as const;

export type PricingFeatureRow = {
  label: string;
  included: boolean;
};

export function getWatchdogPricingFeatureRows(
  plan: {
    watchdog: string;
    report: string;
    webReport: string;
    additional: readonly string[];
  },
  language: Language,
): PricingFeatureRow[] {
  const copy =
    language === "cs"
      ? {
          summaryOverview: "Souhrnný přehled nemovitostí",
          onlineSupport: "Úprava hledání na míru",
        }
      : {
          summaryOverview: "Property overview",
          onlineSupport: "Custom search setup",
        };

  return [
    { label: plan.watchdog, included: true },
    { label: copy.summaryOverview, included: true },
    { label: plan.report, included: true },
    { label: plan.webReport, included: Boolean(plan.webReport) },
    {
      label: copy.onlineSupport,
      included: plan.additional.includes(copy.onlineSupport),
    },
  ];
}

export const PRICING_PLANS = [
  {
    name: "Základní",
    value: "BASIC",
    description: "Vyzkoušejte bez rizika. 14 dní zcela zdarma.",
    watchdog: "1 hlídací pes",
    watchdogCount: 1,
    image: "/image/membership/star2.png",
    report: "Okamžitý email po nalezení",
    webReport: "Detailní analýza hledaných nemovitostí",
    additional: [],
    portfolioFeatures: getPortfolioPricingFeatures(ESTATE_LIMITS.BASIC),
    price: "Zdarma",
    priceSub: "14 dní",
    priceOriginal: 0,
    featured: false,
  },
  {
    name: "Standardní",
    value: "STANDARD",
    description: "Finance a provoz menšího portfolia.",
    watchdog: "Až 2 hlídací psi",
    watchdogCount: 2,
    report: "Emailový report jednou denně",
    webReport: "",
    additional: [],
    portfolioFeatures: getPortfolioPricingFeatures(ESTATE_LIMITS.STANDARD),
    image: "/image/membership/star3.png",
    price: "49 Kč",
    priceSub: "/ měsíc",
    priceOriginal: 199,
    featured: false,
  },
  {
    name: "Prémiový",
    value: "PREMIUM",
    description: "Přehled pro rostoucí portfolio.",
    watchdog: "Až 3 hlídací psi",
    watchdogCount: 3,
    report: "Email do 2 hodin od nalezení",
    webReport: "",
    additional: [],
    portfolioFeatures: getPortfolioPricingFeatures(ESTATE_LIMITS.PREMIUM),
    image: "/image/membership/star4.png",
    price: "129 Kč",
    priceSub: "/ měsíc",
    priceOriginal: 499,
    featured: false,
  },
  {
    name: "Profesionální",
    value: "PROFESSIONAL",
    description: "Nástroje pro správu většího portfolia.",
    watchdog: "Až 5 hlídacích psů",
    watchdogCount: 5,
    report: "Okamžitý email po nalezení",
    webReport: "Detailní analýza hledaných nemovitostí",
    additional: ["Úprava hledání na míru"],
    portfolioFeatures: getPortfolioPricingFeatures(ESTATE_LIMITS.PROFESSIONAL),
    image: "/image/membership/star5.png",
    price: "249 Kč",
    priceSub: "/ měsíc",
    priceOriginal: 1299,
    featured: true,
  },
] as const;

export function getSiteContent(language: Language) {
  const heroBadges = pick(language, {
    cs: HERO_BADGES,
    en: [
      "14 days free",
      "No commitment",
      "Instant notifications",
      "Cheaper than one coffee",
    ] as const,
  });

  const trustStats = pick(language, {
    cs: TRUST_STATS,
    en: [
      { value: "24 hours", label: "monitoring every day" },
      { value: "all", label: "important real estate websites" },
      { value: "max 1 min", label: "notification delay" },
      { value: "free", label: "14 days with no commitment" },
    ] as const,
  });

  const footerNavLinks = pick(language, {
    cs: FOOTER_NAV_LINKS,
    en: [
      { label: "What is it?", href: "/#produkty" },
      { label: "Pricing", href: "/#cenik" },
      { label: "Demo", href: "/#ukazka" },
      { label: "Blog", href: "/blog" },
      { label: "Monitored properties", href: "/sledovane-nemovitosti" },
      { label: "Watchdog comparison", href: "/srovnani-hlidacich-psu" },
    ] as const,
  });

  const legalLinks = pick(language, {
    cs: LEGAL_LINKS,
    en: [
      { title: "Terms and conditions", href: "/obchodni-podminky" },
      { title: "Subscription terms", href: "/podminky-predplatneho" },
      { title: "Personal data processing", href: "/zpracovani-osobnich-udaju" },
    ] as const,
  });

  const infoRows = pick(language, {
    cs: INFO_ROWS,
    en: [
      { label: "Email", value: "info@softiq.cz" },
      { label: "Business entity", value: "Ing. Martin Švejda" },
      { label: "Company ID", value: "21086231" },
      {
        label: "Registered office",
        value: "Janského 559/13, 779 00, Olomouc - Povel",
      },
    ] as const,
  });

  const productMenuItems = pick(language, {
    cs: PRODUCT_MENU_ITEMS,
    en: [
      {
        name: "Basic",
        description:
          "1 property in your portfolio. 1 watchdog for new market listings.",
        href: "/#cenik",
        icon: ChartPieIcon,
      },
      {
        name: "Standard",
        description:
          "Up to 2 properties in your portfolio. Up to 2 watchdogs for new market listings.",
        href: "/#cenik",
        icon: CursorArrowRaysIcon,
      },
      {
        name: "Premium",
        description:
          "Up to 5 properties in your portfolio. Up to 3 watchdogs for new market listings.",
        href: "/#cenik",
        icon: FingerPrintIcon,
      },
      {
        name: "Professional",
        description:
          "Up to 15 properties in your portfolio. Up to 5 watchdogs for new market listings.",
        href: "/#cenik",
        icon: SquaresPlusIcon,
      },
    ] as const,
  });

  const navbarItems = pick(language, {
    cs: NAVBAR_ITEMS,
    en: [
      { name: "What is it?", href: "/#produkty", icon: QuestionMarkCircleIcon },
      { name: "Property portfolio", href: "/portfolio", icon: ChartPieIcon },
      { name: "Watchdog", href: "/hlidaci-pes", icon: CursorArrowRaysIcon },
      { name: "Login", href: "/#prihlaseni", icon: UserIcon },
      { name: "Register", href: "/#prihlaseni", icon: UserPlusIcon },
      { name: "Pricing", href: "/#cenik", icon: BanknotesIcon },
      { name: "Demo", href: "/#ukazka", icon: CursorArrowRaysIcon },
      { name: "FAQ", href: "/#faq", icon: QuestionMarkCircleIcon },
      { name: "Blog", href: "/blog", icon: DocumentTextIcon },
    ] as const,
  });

  const profileItems = pick(language, {
    cs: PROFILE_ITEMS,
    en: [
      { name: "Change membership", href: "", icon: PencilIcon },
      { name: "Change payment details", href: "", icon: CreditCardIcon },
      { name: "Change billing details", href: "", icon: DocumentTextIcon },
      { name: "Change password", href: "", icon: KeyIcon },
      { name: "Delete account", href: "", icon: DestructiveTrashIcon },
    ] as const,
  });

  const settingItems = pick(language, {
    cs: SETTINGS_ITEMS,
    en: [
      { name: "Report a bug", href: "", icon: BugAntIcon },
      { name: "Request a new feature", href: "", icon: LightBulbIcon },
    ] as const,
  });

  const pricingPlans = pick(language, {
    cs: PRICING_PLANS,
    en: [
      {
        name: "Basic",
        value: "BASIC",
        description: "Try it risk-free. 14 days completely free.",
        watchdog: "1 watchdog",
        watchdogCount: 1,
        image: "/image/membership/star2.png",
        report: "Instant email after a listing is found",
        webReport: "Detailed analysis of matching properties",
        additional: [],
        portfolioFeatures: getPortfolioPricingFeaturesEn(ESTATE_LIMITS.BASIC),
        price: "Free",
        priceSub: "14 days",
        priceOriginal: 0,
        featured: false,
      },
      {
        name: "Standard",
        value: "STANDARD",
        description: "Finances and operations for a small portfolio.",
        watchdog: "Up to 2 watchdogs",
        watchdogCount: 2,
        report: "Email report once a day",
        webReport: "",
        additional: [],
        portfolioFeatures: getPortfolioPricingFeaturesEn(
          ESTATE_LIMITS.STANDARD,
        ),
        image: "/image/membership/star3.png",
        price: "49 CZK",
        priceSub: "/ month",
        priceOriginal: 199,
        featured: false,
      },
      {
        name: "Premium",
        value: "PREMIUM",
        description: "A clear overview for a growing portfolio.",
        watchdog: "Up to 3 watchdogs",
        watchdogCount: 3,
        report: "Email within 2 hours of finding a listing",
        webReport: "",
        additional: [],
        portfolioFeatures: getPortfolioPricingFeaturesEn(ESTATE_LIMITS.PREMIUM),
        image: "/image/membership/star4.png",
        price: "129 CZK",
        priceSub: "/ month",
        priceOriginal: 499,
        featured: false,
      },
      {
        name: "Professional",
        value: "PROFESSIONAL",
        description: "Tools for managing a larger portfolio.",
        watchdog: "Up to 5 watchdogs",
        watchdogCount: 5,
        report: "Instant email after a listing is found",
        webReport: "Detailed analysis of matching properties",
        additional: ["Custom search setup"],
        portfolioFeatures: getPortfolioPricingFeaturesEn(
          ESTATE_LIMITS.PROFESSIONAL,
        ),
        image: "/image/membership/star5.png",
        price: "249 CZK",
        priceSub: "/ month",
        priceOriginal: 1299,
        featured: true,
      },
    ] as const,
  });

  return {
    footerNavLinks,
    heroBadges,
    infoRows,
    legalLinks,
    navbarItems,
    pricingPlans,
    productMenuItems,
    profileItems,
    settingItems,
    trustStats,
  };
}
